import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Money, Plus, PencilSimple, Eye, LockKey, ChatCircleDots, PaperPlaneRight, ShieldCheck,
  UsersThree, DownloadSimple, FileText, CalendarCheck, FileCsv, EnvelopeSimple, Receipt, Bank,
  Archive, ArrowClockwise, Trash, BellRinging,
} from "@phosphor-icons/react";

const PAYMENT_METHODS = ["Bank Transfer", "Check", "Cash"];
const STATUS_BADGE = {
  Open: "bg-warning/10 text-warning border-warning/20",
  Reviewed: "bg-primary/10 text-primary border-primary/20",
  Resolved: "bg-success/10 text-success border-success/20",
};

const peso = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? (v || "—") : `₱${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const EARNINGS = [
  ["basic_salary", "Basic Salary"],
  ["night_differential", "Night Differential"],
  ["overtime_pay", "Overtime Pay"],
  ["rest_day_duty", "Rest Day Duty"],
  ["legal_holiday", "Legal Holiday"],
  ["special_holiday", "Special Holiday"],
  ["allowances", "Allowances"],
];
const LOCATIONS = ["Manila", "Cebu", "Admin"];
const LESS = [
  ["absences", "Absences"],
  ["tardiness", "Tardiness"],
  ["undertime", "Undertime"],
];
const MANDATED = [
  ["withholding_tax", "Withholding Tax"],
  ["sss", "SSS"],
  ["pagibig", "Pag-IBIG"],
  ["philhealth", "PhilHealth"],
];
const OTHER = [
  ["loans_advances", "Loans/Advances"],
  ["others", "Others"],
];

function AutoContribButton({ form, setForm }) {
  const run = async () => {
    if (!form.basic_salary) { toast.error("Enter basic salary first"); return; }
    try {
      const { data } = await api.get("/payroll/contributions", { params: { basic_salary: form.basic_salary } });
      setForm((f) => ({ ...f, sss: data.sss, pagibig: data.pagibig, philhealth: data.philhealth }));
      toast.success("Contributions auto-filled from basic salary");
    } catch (e) {
      toast.error("Could not compute contributions");
    }
  };
  return (
    <button type="button" onClick={run} data-testid="autofill-contributions"
      className="text-xs text-primary font-medium hover:underline">Auto-fill from basic salary</button>
  );
}

function YtdCard({ label, value, highlight }) {
  return (
    <Card className={`p-5 border-border shadow-none ${highlight ? "bg-primary text-primary-foreground" : ""}`}>
      <p className={`text-xs uppercase tracking-[0.15em] ${highlight ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{label}</p>
      <p className="mt-2 text-2xl font-heading font-extrabold">{peso(String(value))}</p>
    </Card>
  );
}

function MoneyGroup({ title, fields, form, setForm }) {
  return (
    <div>
      <p className="uppercase text-xs tracking-[0.2em] text-primary font-medium mb-2">{title}</p>
      <div className="grid grid-cols-2 gap-3">
        {fields.map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs">{label}</Label>
            <Input type="number" step="0.01" data-testid={`payroll-${k}`} value={form[k] || ""}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
          </div>
        ))}
      </div>
    </div>
  );
}

function TotalRow({ label, value, strong }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${strong ? "border-t border-border mt-1 pt-2" : ""}`}>
      <span className={strong ? "font-semibold" : "text-muted-foreground"}>{label}</span>
      <span className={strong ? "font-heading font-bold" : "font-medium"}>{peso(String(value || 0))}</span>
    </div>
  );
}

function BreakGroup({ title, fields, data, negative }) {
  const rows = fields.filter(([k]) => data[k] !== "" && data[k] != null && parseFloat(data[k]) !== 0);
  const subtotal = fields.reduce((s, [k]) => s + (parseFloat(data[k]) || 0), 0);
  return (
    <div data-testid={`break-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <p className="uppercase text-xs tracking-[0.2em] text-muted-foreground font-medium mb-1">{title}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-1">—</p>
      ) : rows.map(([k, label]) => (
        <div key={k} className="flex justify-between py-1 text-sm">
          <span className="text-muted-foreground">{label}</span>
          <span className="font-medium">{negative ? "- " : ""}{peso(data[k])}</span>
        </div>
      ))}
      <div className="flex justify-between py-1 text-sm border-t border-border mt-1">
        <span className="font-medium">Subtotal</span>
        <span className="font-semibold">{negative ? "- " : ""}{peso(String(subtotal))}</span>
      </div>
    </div>
  );
}

export default function Payroll() {
  const { isAdmin } = useAuth();
  return isAdmin ? <PayrollAdmin /> : <PayrollEmployee />;
}

function PageHeader({ subtitle }) {
  return (
    <div className="flex items-start gap-4">
      <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center shrink-0">
        <Money weight="duotone" className="h-6 w-6 text-primary-foreground" />
      </div>
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-muted-foreground">Compensation</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Payroll</h1>
        <p className="text-muted-foreground mt-2 flex items-center gap-1.5 text-sm">
          <LockKey className="h-4 w-4" /> {subtitle}
        </p>
      </div>
    </div>
  );
}

const emptyForm = {
  employee_id: "", location: "", pay_period_start: "", pay_period_end: "", basic_salary: "", overtime_pay: "",
  bonuses: "", deductions: "", net_pay: "", payment_method: "Bank Transfer", admin_notes: "",
};

function PayrollAdmin() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detailId, setDetailId] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [thirteenthOpen, setThirteenthOpen] = useState(false);
  const [contribOpen, setContribOpen] = useState(false);
  const [fundingOpen, setFundingOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [load]);

  const autoNet = (f) => {
    const earn = EARNINGS.reduce((s, [k]) => s + (parseFloat(f[k]) || 0), 0);
    const ded = [...LESS, ...MANDATED, ...OTHER].reduce((s, [k]) => s + (parseFloat(f[k]) || 0), 0);
    const n = earn - ded;
    return n ? String(n) : "";
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...emptyForm, ...r, location: r.location || "" });
    setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const eid = editing ? editing.employee_id : form.employee_id;
      if (form.location && eid) {
        await api.put(`/employees/${eid}`, { location: form.location }).catch(() => {});
      }
      if (editing) {
        const { employee_id, location, ...rest } = form;
        await api.put(`/payroll/${editing.id}`, rest);
        toast.success("Payroll updated");
      } else {
        const { location, ...rest } = form;
        await api.post("/payroll", rest);
        toast.success("Payroll created & employee notified");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const archiveRow = async (id) => {
    try {
      await api.put(`/payroll/${id}/archive`);
      toast.success("Payslip archived");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-admin-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader subtitle="Create and manage payslips. Salary and deductions are encrypted at rest." />
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setSummaryOpen(true)} data-testid="period-summary-button" className="rounded-lg gap-2 font-semibold">
            <FileText weight="bold" className="h-4 w-4" /> Period Summary
          </Button>
          <Button variant="outline" onClick={() => setThirteenthOpen(true)} data-testid="thirteenth-button" className="rounded-lg gap-2 font-semibold">
            <CalendarCheck weight="bold" className="h-4 w-4" /> 13th Month
          </Button>
          <Button variant="outline" onClick={() => setContribOpen(true)} data-testid="contribution-report-button" className="rounded-lg gap-2 font-semibold">
            <Receipt weight="bold" className="h-4 w-4" /> Contributions
          </Button>
          <Button variant="outline" onClick={() => setFundingOpen(true)} data-testid="funding-summary-button" className="rounded-lg gap-2 font-semibold">
            <Bank weight="bold" className="h-4 w-4" /> Funding Summary
          </Button>
          <Button variant="outline" onClick={() => setNotifyOpen(true)} data-testid="notify-period-button" className="rounded-lg gap-2 font-semibold">
            <BellRinging weight="bold" className="h-4 w-4" /> Notify Period
          </Button>
          <Button variant="outline" onClick={() => setArchiveOpen(true)} data-testid="archive-view-button" className="rounded-lg gap-2 font-semibold">
            <Archive weight="bold" className="h-4 w-4" /> Archive
          </Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-payroll-button" className="rounded-lg gap-2 font-semibold">
            <UsersThree weight="bold" className="h-4 w-4" /> Bulk Run
          </Button>
          <Button onClick={openCreate} data-testid="add-payroll-button" className="rounded-lg gap-2 font-semibold">
            <Plus weight="bold" className="h-4 w-4" /> Add Payslip
          </Button>
        </div>
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>Pay Period</TableHead>
                <TableHead>Net Pay</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No payroll records yet.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`payroll-row-${r.id}`} className="animate-fade-up">
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.pay_period_start} → {r.pay_period_end}</TableCell>
                  <TableCell className="font-semibold">{peso(r.net_pay)}</TableCell>
                  <TableCell className="text-muted-foreground">{r.payment_method}</TableCell>
                  <TableCell>
                    {r.notified ? (
                      <Badge className="bg-success/10 text-success border-success/20" data-testid={`status-notified-${r.id}`}>Notified</Badge>
                    ) : (
                      <Badge className="bg-warning/10 text-warning border-warning/20" data-testid={`status-pending-${r.id}`}>Pending</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`view-payroll-${r.id}`}
                        onClick={() => setDetailId(r.id)}><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-payroll-${r.id}`}
                        onClick={() => openEdit(r)}><PencilSimple className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`archive-payroll-${r.id}`}
                        onClick={() => archiveRow(r.id)}><Archive className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Payslip" : "Add Payslip"}</DialogTitle>
            <DialogDescription>Amounts for salary and deductions are stored encrypted.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 max-h-[62vh] overflow-y-auto pr-1" data-testid="payroll-form">
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select value={form.location} onValueChange={(v) => setForm({ ...form, location: v })}>
                <SelectTrigger data-testid="payroll-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label>Employee</Label>
                <Select value={form.employee_id} onValueChange={async (v) => {
                  const emp = employees.find((e) => e.id === v);
                  setForm((f) => ({ ...f, employee_id: v, location: emp?.location || f.location }));
                  try {
                    const { data } = await api.get(`/payroll/loan-suggestion/${v}`);
                    if (data.suggested > 0) {
                      setForm((f) => ({ ...f, loans_advances: String(data.suggested) }));
                      toast.success(`Pre-filled Loans/Advances with ₱${Number(data.suggested).toLocaleString()} (approved monthly deduction)`);
                    } else {
                      setForm((f) => ({ ...f, loans_advances: "" }));
                    }
                  } catch (_) { /* no suggestion */ }
                  try {
                    const { data } = await api.get(`/payroll/overtime-suggestion/${v}`);
                    if (data.suggested > 0) {
                      setForm((f) => ({ ...f, overtime_pay: String(data.suggested) }));
                      toast.success(`Pre-filled Overtime Pay with ₱${Number(data.suggested).toLocaleString()} (${data.hours} approved hrs × ₱${data.hourly_rate}/hr)`);
                    } else {
                      setForm((f) => ({ ...f, overtime_pay: "" }));
                    }
                  } catch (_) { /* no suggestion */ }
                }}>
                  <SelectTrigger data-testid="payroll-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Pay period start</Label>
                <Input type="date" data-testid="payroll-start" value={form.pay_period_start}
                  onChange={(e) => setForm({ ...form, pay_period_start: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label>Pay period end</Label>
                <Input type="date" data-testid="payroll-end" value={form.pay_period_end}
                  onChange={(e) => setForm({ ...form, pay_period_end: e.target.value })} required />
              </div>
            </div>
            <MoneyGroup title="Earnings" fields={EARNINGS} form={form} setForm={setForm} />
            <MoneyGroup title="Less: Deductions" fields={LESS} form={form} setForm={setForm} />
            <div className="flex justify-end"><AutoContribButton form={form} setForm={setForm} /></div>
            <MoneyGroup title="Mandated Deductions" fields={MANDATED} form={form} setForm={setForm} />
            <MoneyGroup title="Other Deductions" fields={OTHER} form={form} setForm={setForm} />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1"><LockKey className="h-3 w-3" /> Net pay</Label>
                  <button type="button" className="text-xs text-primary font-medium hover:underline"
                    onClick={() => setForm((f) => ({ ...f, net_pay: autoNet(f) }))} data-testid="payroll-autonet">Auto-calculate</button>
                </div>
                <Input type="number" step="0.01" data-testid="payroll-net_pay" value={form.net_pay}
                  onChange={(e) => setForm({ ...form, net_pay: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Payment method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="payroll-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Admin notes (optional)</Label>
                <Textarea data-testid="payroll-notes" value={form.admin_notes}
                  onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-payroll-button" className="font-semibold">
                {editing ? "Save changes" : "Create & notify"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {detailId && <PayrollDetail payrollId={detailId} isAdmin onClose={() => setDetailId(null)} />}
      <BulkDialog open={bulkOpen} onOpenChange={setBulkOpen} employees={employees} onDone={load} />
      <PayrollSummaryDialog open={summaryOpen} onOpenChange={setSummaryOpen} />
      <ThirteenthDialog open={thirteenthOpen} onOpenChange={setThirteenthOpen} />
      <ContributionReportDialog open={contribOpen} onOpenChange={setContribOpen} />
      <FundingSummaryDialog open={fundingOpen} onOpenChange={setFundingOpen} />
      <NotifyDialog open={notifyOpen} onOpenChange={setNotifyOpen} onDone={load} />
      <ArchiveDialog open={archiveOpen} onOpenChange={setArchiveOpen} onChanged={load} />
    </div>
  );
}

function NotifyDialog({ open, onOpenChange, onDone }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!start || !end) { toast.error("Select pay period start and end"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/notify", { start, end });
      if (!data.total) toast.info("No payslips found for that period");
      else toast.success(`Notified ${data.notified} employee(s)${data.skipped ? ` · ${data.skipped} already notified` : ""}`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="notify-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Notify Employees</DialogTitle>
          <DialogDescription>Sends the payslip notification and email to everyone with a payslip in this pay period. Already-notified employees are skipped.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Pay period start</Label>
            <Input type="date" data-testid="notify-start" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Pay period end</Label>
            <Input type="date" data-testid="notify-end" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={busy} data-testid="notify-send-button" className="gap-2 font-semibold">
            <BellRinging weight="bold" className="h-4 w-4" /> {busy ? "Sending..." : "Notify period"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveDialog({ open, onOpenChange, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll", { params: { archived: true } });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) { setConfirmId(null); load(); } }, [open, load]);

  const restore = async (id) => {
    try { await api.put(`/payroll/${id}/restore`); toast.success("Payslip restored"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/payroll/${id}`); toast.success("Payslip permanently deleted"); setConfirmId(null); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="archive-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Archived Payslips</DialogTitle>
          <DialogDescription>Restore a payslip back to the active list, or permanently delete it. Permanent delete cannot be undone.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>Pay Period</TableHead>
                <TableHead>Net Pay</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No archived payslips.</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} data-testid={`archive-row-${r.id}`}>
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{r.pay_period_start} → {r.pay_period_end}</TableCell>
                  <TableCell className="font-semibold">{peso(r.net_pay)}</TableCell>
                  <TableCell className="text-right">
                    {confirmId === r.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-muted-foreground">Delete permanently?</span>
                        <Button variant="destructive" size="sm" data-testid={`confirm-delete-${r.id}`} onClick={() => remove(r.id)}>Confirm</Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirmId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="gap-1.5" data-testid={`restore-payroll-${r.id}`} onClick={() => restore(r.id)}>
                          <ArrowClockwise className="h-4 w-4" /> Restore
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid={`delete-payroll-${r.id}`} onClick={() => setConfirmId(r.id)}>
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkDialog({ open, onOpenChange, employees, onDone }) {
  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))];
  const money = Object.fromEntries([...EARNINGS, ...LESS, ...MANDATED, ...OTHER].map(([k]) => [k, ""]));
  const [f, setF] = useState({ department: "", pay_period_start: "", pay_period_end: "", payment_method: "Bank Transfer", admin_notes: "", net_pay: "", ...money });
  const [busy, setBusy] = useState(false);

  const autoNet = () => {
    const earn = EARNINGS.reduce((s, [k]) => s + (parseFloat(f[k]) || 0), 0);
    const ded = [...LESS, ...MANDATED, ...OTHER].reduce((s, [k]) => s + (parseFloat(f[k]) || 0), 0);
    return String(earn - ded);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.department) { toast.error("Select a department"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payroll/bulk", f);
      toast.success(`Generated ${data.created} payslip(s)` + (data.skipped ? `, skipped ${data.skipped} existing` : ""));
      onOpenChange(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk Payroll Run</DialogTitle>
          <DialogDescription>Generate itemized payslips for every employee in a department for one pay period. Anyone who already has a payslip for this period is skipped.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 max-h-[62vh] overflow-y-auto pr-1" data-testid="bulk-payroll-form">
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
              <SelectTrigger data-testid="bulk-department"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>
                {departments.length === 0
                  ? <div className="px-3 py-2 text-sm text-muted-foreground">No departments set on employees</div>
                  : departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Pay period start</Label>
              <Input type="date" data-testid="bulk-start" value={f.pay_period_start} onChange={(e) => setF({ ...f, pay_period_start: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label>Pay period end</Label>
              <Input type="date" data-testid="bulk-end" value={f.pay_period_end} onChange={(e) => setF({ ...f, pay_period_end: e.target.value })} required /></div>
          </div>
          <p className="text-xs text-muted-foreground">Default amounts applied to each employee in the department (you can fine-tune individuals afterward).</p>
          <MoneyGroup title="Earnings" fields={EARNINGS} form={f} setForm={setF} />
          <MoneyGroup title="Less: Deductions" fields={LESS} form={f} setForm={setF} />
          <div className="flex justify-end"><AutoContribButton form={f} setForm={setF} /></div>
          <MoneyGroup title="Mandated Deductions" fields={MANDATED} form={f} setForm={setF} />
          <MoneyGroup title="Other Deductions" fields={OTHER} form={f} setForm={setF} />
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1"><LockKey className="h-3 w-3" /> Net pay</Label>
              <button type="button" className="text-xs text-primary font-medium hover:underline"
                onClick={() => setF((p) => ({ ...p, net_pay: autoNet() }))} data-testid="bulk-autonet">Auto-calculate</button>
            </div>
            <Input type="number" step="0.01" data-testid="bulk-net_pay" value={f.net_pay} onChange={(e) => setF({ ...f, net_pay: e.target.value })} />
          </div>
          <div className="space-y-1.5"><Label>Payment method</Label>
            <Select value={f.payment_method} onValueChange={(v) => setF({ ...f, payment_method: v })}>
              <SelectTrigger data-testid="bulk-method"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="run-bulk-button" className="font-semibold">{busy ? "Generating..." : "Generate payslips"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PayrollSummaryDialog({ open, onOpenChange }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!range.start || !range.end) { toast.error("Select start and end dates"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/payroll/summary", { params: { start: range.start, end: range.end } });
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/payroll/summary/pdf", { params: { start: range.start, end: range.end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payroll_Summary_${range.start}_${range.end}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Summary downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get("/payroll/summary/csv", { params: { start: range.start, end: range.end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payroll_Summary_${range.start}_${range.end}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setSummary(null); }}>
      <DialogContent className="max-w-2xl" data-testid="summary-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Payroll Period Summary</DialogTitle>
          <DialogDescription>Summarize all payslips for a pay period, then print or download.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Pay period start</Label>
            <Input type="date" data-testid="summary-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Pay period end</Label>
            <Input type="date" data-testid="summary-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
          </div>
          <Button onClick={generate} disabled={busy} data-testid="generate-summary-button" className="font-semibold">
            {busy ? "Loading..." : "Generate"}
          </Button>
          {summary && summary.count > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={downloadCsv} data-testid="download-summary-csv" className="gap-1.5">
                <FileCsv className="h-4 w-4" /> Export CSV
              </Button>
              <Button variant="outline" onClick={downloadPdf} data-testid="download-summary-pdf" className="gap-1.5">
                <DownloadSimple className="h-4 w-4" /> Print / Download PDF
              </Button>
            </div>
          )}
        </div>

        {summary && (
          <div className="mt-2 max-h-[52vh] overflow-y-auto" data-testid="summary-result">
            {summary.count === 0 ? (
              <p className="text-center py-10 text-muted-foreground">No payslips found for this period.</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Employee</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">Deductions</TableHead>
                      <TableHead className="text-right">Net Pay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.rows.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{r.employee_name}</TableCell>
                        <TableCell className="text-right">{peso(String(r.gross))}</TableCell>
                        <TableCell className="text-right text-muted-foreground">- {peso(String(r.deductions))}</TableCell>
                        <TableCell className="text-right font-semibold">{peso(String(r.net))}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/40 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right">{peso(String(summary.totals.gross))}</TableCell>
                      <TableCell className="text-right">- {peso(String(summary.totals.deductions))}</TableCell>
                      <TableCell className="text-right">{peso(String(summary.totals.net))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <div className="mt-6 space-y-4" data-testid="summary-totals-breakdown">
                  <div className="rounded-lg border border-border p-4 sm:max-w-sm">
                    <p className="uppercase text-xs tracking-[0.2em] text-primary font-medium mb-2">Allowances (Total)</p>
                    <TotalRow label="Allowances" strong value={summary.totals.allowances} />
                  </div>
                  <div className="rounded-lg border border-border p-4 sm:max-w-sm">
                    <p className="uppercase text-xs tracking-[0.2em] text-primary font-medium mb-2">Mandated Deductions (Total)</p>
                    <TotalRow label="SSS" value={summary.totals.sss} />
                    <TotalRow label="PhilHealth" value={summary.totals.philhealth} />
                    <TotalRow label="Pag-IBIG" value={summary.totals.pagibig} />
                    <TotalRow label="Withholding Tax" value={summary.totals.withholding_tax} />
                    <TotalRow label="Total" strong value={(summary.totals.sss || 0) + (summary.totals.philhealth || 0) + (summary.totals.pagibig || 0) + (summary.totals.withholding_tax || 0)} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground italic text-center mt-4">
                  This is a system-generated report. No signature required.
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ThirteenthDialog({ open, onOpenChange }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/payroll/thirteenth", { params: { year } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/payroll/thirteenth/${fmt}`, { params: { year }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `13th_Month_${year}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setData(null); }}>
      <DialogContent className="max-w-2xl" data-testid="thirteenth-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">13th Month Pay</DialogTitle>
          <DialogDescription>Computed per employee as total basic salary earned in the year ÷ 12.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Year</Label>
            <Input type="number" data-testid="thirteenth-year" value={year} onChange={(e) => setYear(e.target.value)} className="w-32" />
          </div>
          <Button onClick={generate} disabled={busy} data-testid="generate-thirteenth-button" className="font-semibold">
            {busy ? "Loading..." : "Compute"}
          </Button>
          {data && data.rows.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={() => download("csv")} data-testid="download-thirteenth-csv" className="gap-1.5">
                <FileCsv className="h-4 w-4" /> CSV
              </Button>
              <Button variant="outline" onClick={() => download("pdf")} data-testid="download-thirteenth-pdf" className="gap-1.5">
                <DownloadSimple className="h-4 w-4" /> PDF
              </Button>
            </div>
          )}
        </div>

        {data && (
          <div className="mt-2 max-h-[52vh] overflow-y-auto" data-testid="thirteenth-result">
            {data.rows.length === 0 ? (
              <p className="text-center py-10 text-muted-foreground">No payslips found for {year}.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Payslips</TableHead>
                    <TableHead className="text-right">Total Basic (YTD)</TableHead>
                    <TableHead className="text-right">13th Month Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{r.employee_name}</TableCell>
                      <TableCell className="text-right text-muted-foreground">{r.months}</TableCell>
                      <TableCell className="text-right">{peso(String(r.total_basic))}</TableCell>
                      <TableCell className="text-right font-semibold">{peso(String(r.thirteenth))}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/40 font-bold">
                    <TableCell colSpan={3}>TOTAL</TableCell>
                    <TableCell className="text-right">{peso(String(data.total))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ContributionReportDialog({ open, onOpenChange }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!range.start || !range.end) { toast.error("Select start and end dates"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/payroll/contributions-report", { params: { start: range.start, end: range.end } });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/payroll/contributions-report/${fmt}`, { params: { start: range.start, end: range.end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Contributions_${range.start}_${range.end}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setReport(null); }}>
      <DialogContent className="max-w-2xl" data-testid="contribution-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Statutory Contributions Report</DialogTitle>
          <DialogDescription>Monthly SSS, PhilHealth and Pag-IBIG remittance summary (employee share) for a pay period.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label>Pay period start</Label>
            <Input type="date" data-testid="contrib-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Pay period end</Label>
            <Input type="date" data-testid="contrib-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} />
          </div>
          <Button onClick={generate} disabled={busy} data-testid="generate-contrib-button" className="font-semibold">
            {busy ? "Loading..." : "Generate"}
          </Button>
          {report && report.rows.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={() => download("csv")} data-testid="download-contrib-csv" className="gap-1.5">
                <FileCsv className="h-4 w-4" /> CSV
              </Button>
              <Button variant="outline" onClick={() => download("pdf")} data-testid="download-contrib-pdf" className="gap-1.5">
                <DownloadSimple className="h-4 w-4" /> PDF
              </Button>
            </div>
          )}
        </div>

        {report && (
          <div className="mt-2 max-h-[52vh] overflow-y-auto" data-testid="contrib-result">
            {report.rows.length === 0 ? (
              <p className="text-center py-10 text-muted-foreground">No payslips found for this period.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">SSS</TableHead>
                    <TableHead className="text-right">PhilHealth</TableHead>
                    <TableHead className="text-right">Pag-IBIG</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{r.employee_name}</TableCell>
                      <TableCell className="text-right">{peso(String(r.sss))}</TableCell>
                      <TableCell className="text-right">{peso(String(r.philhealth))}</TableCell>
                      <TableCell className="text-right">{peso(String(r.pagibig))}</TableCell>
                      <TableCell className="text-right font-semibold">{peso(String(r.total))}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/40 font-bold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell className="text-right">{peso(String(report.totals.sss))}</TableCell>
                    <TableCell className="text-right">{peso(String(report.totals.philhealth))}</TableCell>
                    <TableCell className="text-right">{peso(String(report.totals.pagibig))}</TableCell>
                    <TableCell className="text-right">{peso(String(report.totals.total))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FundingSummaryDialog({ open, onOpenChange }) {
  const [f, setF] = useState({ start: "", end: "", pay_date: "", allowances: "", sss: "", phic: "", hdmf: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = () => ({
    start: f.start, end: f.end, pay_date: f.pay_date,
    allowances: f.allowances || "0", sss: f.sss || "0", phic: f.phic || "0", hdmf: f.hdmf || "0",
  });

  const generate = async () => {
    if (!f.start || !f.end) { toast.error("Select cut-off start and end"); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/payroll/funding-summary", { params: params() });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const download = async (fmt) => {
    try {
      const res = await api.get(`/payroll/funding-summary/${fmt}`, { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Funding_Summary_${f.start}_${f.end}.${fmt}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error("Download failed"); }
  };

  const line = (label, value, opts = {}) => (
    <div key={opts.key} className={`flex justify-between px-3 py-2 text-sm ${opts.top ? "border-t border-border" : ""} ${opts.total ? "bg-primary text-primary-foreground rounded-md mt-2 font-heading font-bold" : ""}`}>
      <span className={opts.bold ? "font-semibold" : (opts.indent ? "text-muted-foreground pl-3" : "")}>{label}</span>
      <span className={opts.bold || opts.total ? "font-semibold" : "font-medium"}>{peso(String(value || 0))}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setData(null); }}>
      <DialogContent className="max-w-2xl" data-testid="funding-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Payroll Funding Summary</DialogTitle>
          <DialogDescription>Net pay grouped by location, plus manually-entered allowances and (EE+ER) premium contributions.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Cut-off start</Label>
            <Input type="date" data-testid="funding-start" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Cut-off end</Label>
            <Input type="date" data-testid="funding-end" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Payroll date</Label>
            <Input type="date" data-testid="funding-paydate" value={f.pay_date} onChange={(e) => setF({ ...f, pay_date: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Allowances</Label>
            <Input type="number" step="0.01" data-testid="funding-allowances" value={f.allowances} onChange={(e) => setF({ ...f, allowances: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>SSS (EE+ER)</Label>
            <Input type="number" step="0.01" data-testid="funding-sss" value={f.sss} onChange={(e) => setF({ ...f, sss: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>PHIC (EE+ER)</Label>
            <Input type="number" step="0.01" data-testid="funding-phic" value={f.phic} onChange={(e) => setF({ ...f, phic: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>HDMF (EE+ER)</Label>
            <Input type="number" step="0.01" data-testid="funding-hdmf" value={f.hdmf} onChange={(e) => setF({ ...f, hdmf: e.target.value })} /></div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={generate} disabled={busy} data-testid="generate-funding-button" className="font-semibold">
            {busy ? "Loading..." : "Generate"}
          </Button>
          {data && (
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={() => download("csv")} data-testid="download-funding-csv" className="gap-1.5">
                <FileCsv className="h-4 w-4" /> CSV
              </Button>
              <Button variant="outline" onClick={() => download("pdf")} data-testid="download-funding-pdf" className="gap-1.5">
                <DownloadSimple className="h-4 w-4" /> PDF
              </Button>
            </div>
          )}
        </div>

        {data && (
          <div className="mt-2 rounded-lg border border-border p-4 max-h-[48vh] overflow-y-auto" data-testid="funding-result">
            <p className="font-heading font-bold text-lg">{data.company}</p>
            <p className="text-sm text-muted-foreground">Payroll Cut-off: {data.cut_off_start} to {data.cut_off_end}</p>
            <p className="text-sm text-muted-foreground">Payroll Date: {data.pay_date || "—"}</p>
            <div className="mt-4">
              {data.locations.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground text-sm">No payslips found for this cut-off.</p>
              ) : data.locations.map((l, i) => line(l.label, l.net, { key: i }))}
              {line("Net Pay Sub-total", data.net_total, { top: true, bold: true })}
              {line("ALLOWANCES", data.allowances)}
              {line("Sub-total", data.subtotal, { top: true, bold: true })}
              {line("PREMIUM CONTRI", data.premium.total, { top: true, bold: true })}
              {line("(EE + ER) SSS", data.premium.sss, { indent: true })}
              {line("(EE + ER) PHIC", data.premium.phic, { indent: true })}
              {line("(EE + ER) HDMF", data.premium.hdmf, { indent: true })}
              {line("TOTAL", data.total, { total: true })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PayrollEmployee() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [ytd, setYtd] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payroll/mine");
      setRows(data);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/payroll/ytd").then(({ data }) => setYtd(data)).catch(() => {});
  }, [load]);

  return (
    <div className="space-y-6" data-testid="payroll-employee-page">
      <PageHeader subtitle="View your payslips and raise inquiries with HR." />

      {ytd && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ytd-summary">
          <YtdCard label={`YTD Gross (${ytd.year})`} value={ytd.gross} />
          <YtdCard label="YTD Deductions" value={ytd.deductions} />
          <YtdCard label="YTD Net Pay" value={ytd.net} highlight />
          <YtdCard label="Est. 13th Month" value={ytd.thirteenth} />
        </div>
      )}

      {loading ? (
        <p className="text-center py-12 text-muted-foreground">Loading...</p>
      ) : rows.length === 0 ? (
        <Card className="p-12 border-border shadow-none text-center">
          <Money weight="duotone" className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground">No payslips available yet. You'll be notified when one is published.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => (
            <Card key={r.id} data-testid={`payslip-${r.id}`}
              className="p-6 border-border shadow-none transition-transform duration-200 hover:-translate-y-1 cursor-pointer animate-fade-up"
              onClick={() => setDetailId(r.id)}>
              <div className="flex items-center justify-between">
                <span className="uppercase text-xs tracking-[0.2em] text-muted-foreground">Pay period</span>
                <Badge variant="outline" className="bg-muted text-muted-foreground border-border">{r.payment_method}</Badge>
              </div>
              <p className="text-sm font-medium mt-1">{r.pay_period_start} → {r.pay_period_end}</p>
              <p className="mt-4 text-3xl font-heading font-extrabold tracking-tight">{peso(r.net_pay)}</p>
              <p className="text-sm text-muted-foreground">Net pay</p>
              <Button variant="outline" size="sm" className="mt-4 w-full gap-1.5" data-testid={`view-payslip-${r.id}`}>
                <Eye className="h-4 w-4" /> View details
              </Button>
            </Card>
          ))}
        </div>
      )}

      {detailId && <PayrollDetail payrollId={detailId} isAdmin={false} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function PayrollDetail({ payrollId, isAdmin, onClose }) {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/payroll/${payrollId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      onClose();
    }
  }, [payrollId, onClose]);

  useEffect(() => { load(); }, [load]);

  const submitComment = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/payroll/${payrollId}/comments`, { comment_text: comment.trim() });
      toast.success("Inquiry submitted");
      setComment("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPosting(false);
    }
  };

  const respond = async (cid, status, response) => {
    try {
      await api.put(`/payroll/comments/${cid}`, { status, admin_response: response });
      toast.success("Response saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/payroll/${payrollId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payslip_${(data?.employee_name || "employee").replace(/\s/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success("Payslip downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const emailPayslip = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/payroll/${payrollId}/email`);
      toast.success(`Payslip emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send email");
    } finally {
      setEmailing(false);
    }
  };

  const row = (label, value, strong) => (
    <div className="flex justify-between py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-sm text-right ${strong ? "font-heading font-bold text-base" : "font-medium"}`}>{value}</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payroll-detail">
        <DialogHeader>
          <DialogTitle className="font-heading">Payslip</DialogTitle>
          <DialogDescription>
            {data ? `${data.employee_name} · ${data.pay_period_start} → ${data.pay_period_end}` : "Loading..."}
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <p className="py-8 text-center text-muted-foreground">Loading...</p>
        ) : (
          <div className="max-h-[64vh] overflow-y-auto pr-1 space-y-5">
            <div className="flex justify-end gap-2">
              {isAdmin && (
                <Button variant="outline" size="sm" onClick={emailPayslip} disabled={emailing}
                  data-testid="email-payslip" className="gap-1.5">
                  <EnvelopeSimple className="h-4 w-4" /> {emailing ? "Sending..." : "Email payslip"}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="download-payslip-pdf" className="gap-1.5">
                <DownloadSimple className="h-4 w-4" /> Download PDF
              </Button>
            </div>
            <div className="rounded-lg border border-border p-4 space-y-4">
              <BreakGroup title="Earnings" fields={EARNINGS} data={data} />
              <BreakGroup title="Less: Deductions" fields={LESS} data={data} negative />
              <BreakGroup title="Mandated Deductions" fields={MANDATED} data={data} negative />
              <BreakGroup title="Other Deductions" fields={OTHER} data={data} negative />
              <div className="flex justify-between pt-2 border-t border-border">
                <span className="font-heading font-bold">Net Pay</span>
                <span className="font-heading font-bold text-lg" data-testid="detail-net-pay">{peso(data.net_pay)}</span>
              </div>
              {row("Payment method", data.payment_method)}
            </div>
            {(Number(data.loan_balance_current) > 0 || Number(data.loan_this_period) > 0) && (
              <div className="rounded-lg border border-border p-4" data-testid="loan-summary">
                <p className="uppercase text-xs tracking-[0.2em] text-primary font-medium mb-2">Loan</p>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-muted-foreground">Loan balance (before)</span>
                  <span className="font-medium">{peso(String(data.loan_balance_current))}</span>
                </div>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-muted-foreground">Loan payment this period</span>
                  <span className="font-medium">- {peso(String(data.loan_this_period))}</span>
                </div>
                <div className="flex justify-between py-1 text-sm border-t border-border mt-1 pt-2">
                  <span className="font-semibold">CA Balance (remaining loan)</span>
                  <span className="font-heading font-bold" data-testid="loan-after">{peso(String(data.loan_after))}</span>
                </div>
              </div>
            )}
            {data.admin_notes && (
              <div className="rounded-lg bg-muted/50 border border-border p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Admin notes</p>
                <p className="text-sm">{data.admin_notes}</p>
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-3">
                <ChatCircleDots weight="duotone" className="h-4 w-4 text-primary" />
                <span className="font-heading font-bold text-sm">Inquiries</span>
              </div>

              {!isAdmin && (
                <div className="flex gap-2 mb-4">
                  <Textarea data-testid="comment-input" placeholder="Ask HR about this payslip..." value={comment}
                    onChange={(e) => setComment(e.target.value)} className="min-h-[44px]" />
                  <Button onClick={submitComment} disabled={posting} data-testid="submit-comment-button"
                    className="shrink-0 self-end gap-1.5"><PaperPlaneRight className="h-4 w-4" /> Send</Button>
                </div>
              )}

              <div className="space-y-3">
                {(data.comments || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-lg">No inquiries yet.</p>
                ) : data.comments.map((c) => (
                  <CommentItem key={c.id} c={c} isAdmin={isAdmin} onRespond={respond} />
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CommentItem({ c, isAdmin, onRespond }) {
  const [resp, setResp] = useState(c.admin_response || "");
  const [status, setStatus] = useState(c.status);
  const [editing, setEditing] = useState(false);

  return (
    <div className="rounded-lg border border-border p-3" data-testid={`comment-${c.id}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium">{c.employee_name}</span>
        <Badge variant="outline" className={STATUS_BADGE[c.status]}>{c.status}</Badge>
      </div>
      <p className="text-sm">{c.comment_text}</p>
      <p className="text-[11px] text-muted-foreground mt-1">{new Date(c.date_submitted).toLocaleString()}</p>

      {c.admin_response && !editing && (
        <div className="mt-2 pl-3 border-l-2 border-primary/40">
          <p className="text-xs uppercase tracking-wide text-primary mb-0.5 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" /> HR response {c.responder_name ? `· ${c.responder_name}` : ""}
          </p>
          <p className="text-sm">{c.admin_response}</p>
        </div>
      )}

      {isAdmin && (
        <div className="mt-3">
          {!editing && !c.admin_response ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid={`respond-${c.id}`}>Respond</Button>
          ) : isAdmin && (editing ? (
            <div className="space-y-2">
              <Textarea value={resp} onChange={(e) => setResp(e.target.value)} placeholder="HR reply..."
                data-testid={`response-input-${c.id}`} className="min-h-[44px]" />
              <div className="flex items-center gap-2">
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-9 w-36" data-testid={`response-status-${c.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Reviewed">Reviewed</SelectItem>
                    <SelectItem value="Resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" data-testid={`save-response-${c.id}`}
                  onClick={() => { onRespond(c.id, status, resp); setEditing(false); }} className="font-semibold">Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`edit-response-${c.id}`}>Edit response</Button>
          ))}
        </div>
      )}
    </div>
  );
}
